import { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { sendTestPush } from "@/lib/push";
import {
  oneSignalSupported, oneSignalEnable, oneSignalDisable, oneSignalIsEnabled, oneSignalDiagnostics,
} from "@/lib/onesignal";

// Notifiche push via OneSignal (canale unico, integrato col sistema notifiche).
export const PushToggle = () => {
  const [supported] = useState(oneSignalSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (supported) oneSignalIsEnabled().then(setSubscribed).catch(() => {});
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try { await oneSignalEnable(); setSubscribed(true); toast.success("Notifiche push attivate su questo dispositivo"); }
    catch (e) { toast.error(e?.message || "Impossibile attivare le notifiche"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await oneSignalDisable(); setSubscribed(false); toast.success("Notifiche push disattivate"); }
    catch (e) { toast.error("Errore durante la disattivazione"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    // Diagnostica CLIENT (senza segreti): External ID, Subscription ID, token troncato, optedIn.
    let diag = null;
    try {
      diag = await oneSignalDiagnostics();
      console.info("[Push Test] client diagnostics:", diag);
    } catch (e) { console.debug("diag err", e?.message || e); }
    try {
      const res = await sendTestPush();
      // Log completo per verifica in produzione (visibile in console del browser).
      console.info("[Push Test] backend result:", res);
      if (res?.channel === "onesignal") {
        const parts = [
          res.target_subscriptions ? `Target: ${res.target_subscriptions}` : `Recipients: ${res.recipients}`,
          res.onesignal_id ? `msgID: ${res.onesignal_id}` : null,
          diag?.subscription_id ? `subID: ${diag.subscription_id}` : null,
          diag && diag.external_id_match === false ? "⚠ External ID NON allineato" : null,
        ].filter(Boolean).join(" · ");
        toast.success(res.message || "Notifica inviata correttamente tramite OneSignal", { description: parts, duration: 10000 });
      } else {
        toast.success("Notifica di prova inviata (canale VAPID)");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const detail = (d && typeof d === "object" ? d.message : d) || e?.message || "Invio non riuscito";
      console.warn("[Push Test] error:", e?.response?.data || e);
      toast.error(detail, { duration: 10000 });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="push-toggle-button" aria-label="Notifiche push">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" />
            : subscribed ? <BellRing className="h-5 w-5 text-[hsl(var(--hi-gold))]" />
            : <Bell className="h-5 w-5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifiche push</span>
          <span
            data-testid="push-status"
            className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
              subscribed
                ? "bg-[hsl(var(--hi-gold)/0.18)] text-[hsl(var(--hi-gold))]"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {busy ? "…" : subscribed ? "Attivo" : "Non attivo"}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!subscribed ? (
          <DropdownMenuItem onClick={enable} data-testid="push-enable" disabled={busy}>
            <Bell className="h-4 w-4 mr-2" /> Attiva su questo dispositivo
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem onClick={test} data-testid="push-test">
              <Send className="h-4 w-4 mr-2" /> Invia notifica di prova
            </DropdownMenuItem>
            <DropdownMenuItem onClick={disable} data-testid="push-disable" className="text-destructive">
              <BellOff className="h-4 w-4 mr-2" /> Disattiva su questo dispositivo
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
